import { useState, createContext, useEffect, ReactNode } from 'react';
import { AccountOneInfo, LombardPointItem } from '@/types/account';
import { getAccountOneV3 } from '@/services/account';
import { usePoolInfoContext } from '@/hooks/usePoolInfo';
import { useWallet } from '@/hooks/wallet';
import { useCurrentChainAddress } from '@/hooks/wallet';
import { OperationTab } from '@/types/common';
import { isLBTCvPool, isLombardPool } from '@/utils/common';
import { useStakingStore } from '@/store/staking';

export const AccountInfoContext = createContext<{
  isFetching: boolean;
  accountInfo: AccountOneInfo;
  fetchAccountInfo: () => void;
  lombardPoints?: LombardPointItem[];
}>({
  isFetching: true,
  accountInfo: {} as AccountOneInfo,
  fetchAccountInfo: () => {},
  lombardPoints: [],
});

AccountInfoContext.displayName = 'AccountInfoContext';
interface Props {
  children: ReactNode;
  tab: OperationTab;
}

export const AccountInfoProvider = (props: Props) => {
  const { children, tab } = props;
  const { poolInfo } = usePoolInfoContext();
  const { setRedeemNotAllow } = useStakingStore();
  const [accountInfo, setAccountInfo] = useState<AccountOneInfo>(
    {} as AccountOneInfo
  );
  const [lombardPoints, setLombardPoints] = useState<LombardPointItem[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const { isLogin } = useWallet();
  const { walletAddress } = useCurrentChainAddress(poolInfo?.chainCode);

  const fetchAccountInfo = async () => {
    if (isFetching) return;
    setIsFetching(true);

    const params = {
      poolId: poolInfo.id,
      walletAddress,
    };

    const { result } = await getAccountOneV3(params);
    setAccountInfo(result);

    if (isLombardPool(poolInfo.id) || isLBTCvPool(poolInfo.id)) {
      setLombardPoints(result?.newPoints || []);

      if (
        result?.processingUnstakeAmount &&
        isLBTCvPool(poolInfo.id) &&
        +result.processingUnstakeAmount > 0
      ) {
        setRedeemNotAllow(true);
      } else {
        setRedeemNotAllow(false);
      }
    }
    setIsFetching(false);
  };

  useEffect(() => {
    if (!isLogin) {
      setIsFetching(false);
      setAccountInfo({} as AccountOneInfo);
      setLombardPoints([]);
      return;
    }
    if (isLogin || tab === OperationTab.Account) {
      fetchAccountInfo();
    }
  }, [isLogin, tab]);

  return (
    <AccountInfoContext.Provider
      value={{
        isFetching,
        accountInfo,
        fetchAccountInfo,
        lombardPoints,
      }}
    >
      {children}
    </AccountInfoContext.Provider>
  );
};
